<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useForm } from 'vee-validate';
import { useQueryClient, useMutation } from '@tanstack/vue-query';
import { paymentMovementsApi, type PaymentMovementForm } from '@/api/payment-movements';
import { usePaymentsStore } from '@/stores/payments';
import * as yup from 'yup';
import type { AxiosError } from 'axios';
import PriceInput from '@/components/price-input.vue';

const { t } = useI18n();

interface Props {
  paymentMovement?: PaymentMovementForm | undefined;
  caregiverId: number;
  mode: 'new' | 'edit';
}

const props = defineProps<Props>();

const emit = defineEmits(['close']);

const store = usePaymentsStore();

const validationSchema = yup.object({
  date: yup.date()
    .min(store.fromDate, t('paymentMovementForm.validDateRange'))
    .max(store.toDate, t('paymentMovementForm.validDateRange'))
    .required(t('paymentMovementForm.fieldRequired')),
  movementType: yup.string().required(t('paymentMovementForm.fieldRequired')),
  amount: yup.number()
    .typeError(t('paymentMovementForm.fieldPositive'))
    .min(1, t('paymentMovementForm.fieldPositive'))
    .required(t('paymentMovementForm.fieldRequired')),
  description: yup.string().required(t('paymentMovementForm.fieldRequired')),
  withTaxBill: yup.boolean(),
});

const initialValues = {
  caregiverId: props.caregiverId,
  date: props.paymentMovement?.date,
  movementType: props.paymentMovement?.movementType,
  amount: props.paymentMovement?.amount ? Math.abs(props.paymentMovement.amount) : 0,
  description: props.paymentMovement?.description,
  // A new movement starts out of the tax bill; an edited one shows what it has.
  withTaxBill: props.paymentMovement?.withTaxBill ?? false,
};

const { handleSubmit, values, errors, defineField } = useForm<PaymentMovementForm>({ validationSchema, initialValues });

const [date, dateAttrs] = defineField('date');
const [movementType, movementTypeAttrs] = defineField('movementType');
const [amount, amountAttrs] = defineField('amount');
const [description, descriptionAttrs] = defineField('description');
const [withTaxBill, withTaxBillAttrs] = defineField('withTaxBill');

// Only an adjustment goes into the tax bill (PaymentMovement#clear_tax_bill).
watch(movementType, (type) => {
  if (type !== 'adjustment') withTaxBill.value = false;
});

const queryClient = useQueryClient();

const submitButtonDisabled = ref(false);

type SaveError = AxiosError<{ errors?: Record<string, string[]> }>;

// A refused save can be corrected and sent again.
function enableSubmit() {
  submitButtonDisabled.value = false;
}

const { mutate: createPaymentMovement, error: createError } = useMutation<unknown, SaveError>(
  {
    mutationFn: () => paymentMovementsApi.create({ ...values }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payments'] });
      emit('close');
    },
    onError: enableSubmit,
  },
);

const { mutate: updatePaymentMovement, error: updateError } = useMutation<unknown, SaveError>(
  {
    mutationFn: () => paymentMovementsApi.update({ ...values, id: props.paymentMovement?.id }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payments'] });
      emit('close');
    },
    onError: enableSubmit,
  },
);

// Why the server refused the movement (e.g. a tax bill on a day a contract
// salary pays), or the generic error.
function refusalOf(error: SaveError) {
  return Object.values(error.response?.data?.errors ?? {}).flat()[0];
}

const saveErrorMessage = computed(() => {
  const error = createError.value ?? updateError.value;
  if (!error) return '';

  return refusalOf(error) ?? t('userSession.defaultError');
});

const onSubmit = handleSubmit(() => {
  submitButtonDisabled.value = true;
  if (props.mode === 'edit') {
    updatePaymentMovement();
  } else {
    createPaymentMovement();
  }
});

const movementTypes = [
  { title: 'Restar 💰 al profesional', value: 'payment' },
  { title: 'Sumar 💰 al profesional', value: 'adjustment' },
];
</script>
<template>
  <v-card class="px-4 pb-4">
    <v-card-title class="mb-4 align-baseline d-flex">
      {{ mode === 'new' ? t('paymentMovementForm.createTitle') : t('paymentMovementForm.updateTitle') }}
      <v-spacer />
      <v-btn
        icon="mdi-window-close"
        variant="plain"
        @click="emit('close')"
      />
    </v-card-title>
    <v-form
      class="w-100 ma-0"
      @submit.prevent="onSubmit"
    >
      <v-row>
        <v-select
          v-model="movementType"
          v-bind="movementTypeAttrs"
          class="px-2 v-col-6"
          variant="outlined"
          :items="movementTypes"
          :label="t('paymentMovement.movementType')"
          :error-messages="errors.movementType"
        />
        <v-text-field
          v-model="description"
          v-bind="descriptionAttrs"
          variant="outlined"
          class="px-2 v-col-6"
          :label="t('paymentMovement.description')"
          :error-messages="errors.description"
        />
      </v-row>
      <v-row>
        <price-input
          v-model="amount"
          v-bind="amountAttrs"
          class="px-2 v-col-6"
          :label="t('paymentMovement.amount')"
          :error-messages="errors.amount"
        />
        <v-text-field
          v-model="date"
          v-bind="dateAttrs"
          type="date"
          class="px-2 v-col-6"
          variant="outlined"
          :label="t('paymentMovement.date')"
          :error-messages="errors.date"
        />
      </v-row>
      <v-row v-if="movementType === 'adjustment'">
        <v-checkbox
          v-model="withTaxBill"
          v-bind="withTaxBillAttrs"
          class="px-2 v-col-12"
          :label="t('paymentMovement.withTaxBill')"
          :hint="t('paymentMovementForm.withTaxBillHint')"
          persistent-hint
        />
      </v-row>
      <v-row>
        <v-spacer />
        <v-btn
          color="primary"
          type="submit"
          class="mb-4 mr-4"
          :disabled="submitButtonDisabled"
        >
          {{ mode === 'new' ? t('paymentMovementForm.createSubmit') : t('paymentMovementForm.updateSubmit') }}
        </v-btn>
      </v-row>
    </v-form>
    <v-alert
      v-if="saveErrorMessage"
      class="mt-4 rounded"
      :type="'error'"
    >
      {{ saveErrorMessage }}
    </v-alert>
  </v-card>
</template>
